import type { FC } from 'react';
import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import FormDropdown from '@wix/da-ds/pkg/Dropdown/Preset/FormDropdown';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { useDispatch, useSelector } from 'react-redux';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { updatePaypalState } from '../../../../../actions/studio/earnings';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { getCountryOptions } from '../../../constants';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';
import WithdrawalErrorBlock from '../../_partials/WithdrawalErrorBlock';

import s from './PayPalWithdrawSetupModal.scss';

export interface Props {
  isOpen: boolean;
  requestClose: () => void;
  onSuccess?: () => void;
  onWithdraw?: () => void;
  paypalEmail?: string;
  isMissingCountry?: boolean;
}

const PAYPAL_SIGNUP_URL = 'https://www.paypal.com/signup';

const PayPalWithdrawSetupModal: FC<Props> = ({
  isOpen,
  requestClose,
  onSuccess,
  onWithdraw,
  paypalEmail,
  isMissingCountry,
}) => {
  const { t } = useTranslation();
  const countryOptions = getCountryOptions(t);
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.PAYPAL_CONNECT)
  );
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const isCountryOnlyMode = paypalEmail && isMissingCountry;

  const isFormValid = isCountryOnlyMode
    ? country
    : firstName.trim() && lastName.trim() && email.trim() && country;

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  // Clear error when user starts typing
  const clearError = () => {
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleWithdraw = () => {
    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.PayPalPayouts,
        process_step: 'setup',
      })
    );
    // Map country values to match PAPI types
    const countryValue = country === 'US' ? 'usa' : 'outside_usa';

    const requestData = isCountryOnlyMode
      ? { country: countryValue }
      : {
          firstname: firstName.trim(),
          lastname: lastName.trim(),
          email: email.trim(),
          country: countryValue,
        };

    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.PAYPAL_CONNECT,
        method: SimpleXHRMethod.POST,
        data: requestData,
        onSuccess: (response: any) => {
          const emailToStore = isCountryOnlyMode ? paypalEmail : email.trim();
          const isCountryMissing = false;
          const fee = response?.fee;

          dispatch(
            updatePaypalState({
              email: emailToStore,
              isMissingCountry: isCountryMissing,
              fee,
            })
          );

          onSuccess?.();
        },
        onError: (response: any) => {
          setErrorMessage(
            response?.errorDescription ||
              t('studio.earnings.paypal.withdraw.error.message')
          );
        },
      })
    );
  };

  return (
    <Modal
      className={s['wrapper']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.earnings.withdrawToPayPal.title')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          onClick={requestClose}
          size="large"
          variant="main"
          key="cancel"
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          onClick={handleWithdraw}
          size="large"
          variant="brand-green"
          key="withdraw"
          disabled={!isFormValid || isLoading}
          loading={isLoading}
        >
          {t('studio.earnings.withdrawToPayPal.continue')}
        </BrandedButton>,
      ]}
    >
      <WithdrawalErrorBlock message={errorMessage} />

      {!isCountryOnlyMode && (
        <>
          <div className={s['subtitle']}>
            {t('studio.earnings.withdrawToPayPal.subtitle')}
          </div>

          <div className={s['signup-link']}>
            {t('studio.earnings.withdrawToPayPal.signupPrompt')}{' '}
            <a
              href={PAYPAL_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-link-level-2-primary"
            >
              {t('studio.earnings.withdrawToPayPal.signupLink')}
            </a>
          </div>

          <div className={s['form-title']}>
            {t('studio.earnings.withdrawToPayPal.formTitle')}
          </div>
        </>
      )}

      {isCountryOnlyMode && (
        <div className={s['subtitle']}>
          {t('studio.earnings.withdrawToPayPal.countryRequired')}
        </div>
      )}

      <Flex direction="column" gap={16} className={s['form']}>
        {!isCountryOnlyMode && (
          <Flex direction={isMobile ? 'column' : 'row'} gap={12}>
            <Flex direction="column" flex={1}>
              <TextInput
                id="paypal-first-name"
                label={t('studio.earnings.withdrawToPayPal.firstName')}
                value={firstName}
                onChange={ev => {
                  setFirstName(ev.target.value);
                  clearError();
                }}
                type="text"
                wrapperClassName={s['input']}
              />
            </Flex>
            <Flex direction="column" flex={1}>
              <TextInput
                id="paypal-last-name"
                label={t('studio.earnings.withdrawToPayPal.lastName')}
                value={lastName}
                onChange={ev => {
                  setLastName(ev.target.value);
                  clearError();
                }}
                type="text"
                wrapperClassName={s['input']}
              />
            </Flex>
          </Flex>
        )}

        {!isCountryOnlyMode && (
          <TextInput
            id="paypal-email"
            label={t('studio.earnings.withdrawToPayPal.email')}
            value={email}
            onChange={ev => {
              setEmail(ev.target.value);
              clearError();
            }}
            type="email"
            wrapperClassName={s['input']}
          />
        )}

        <div className={s['country-field']}>
          <label className={s['country-label']}>
            {t('studio.earnings.withdrawToPayPal.country')}
          </label>
          <FormDropdown
            items={countryOptions}
            selectedValue={country}
            onSelect={value => {
              setCountry(value);
              clearError();
            }}
            placeholder={t(
              'studio.earnings.withdrawToPayPal.countryPlaceholder'
            )}
            className={s['input']}
          />
        </div>
      </Flex>
    </Modal>
  );
};

PayPalWithdrawSetupModal.displayName = 'PayPalWithdrawSetupModal';

export default PayPalWithdrawSetupModal;
